import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatAccordion, MatDialogRef } from '@angular/material';
import { Constants } from '@app/shared/class/constants.class';
import { AppConfig } from '@config/app.config';
import { SetMenu } from '@config/menu.config';
import { GeneralsService } from '@core/services/generals.service';
import { RolesService } from '@core/services/roles.service';
import { Menu } from '@shared/interfaces/menu';
import { Access, ActionView, Child, Permition, RolPermitions, Select } from '@shared/interfaces/rol';

@Component({
  selector: 'app-roles-edit',
  templateUrl: './roles-edit.component.html'
})
export class RolesEditComponent implements OnInit {
  public constants = new Constants();

  public rol: RolPermitions;
  public rolForm: FormGroup;
  public menu: Array<any> = [];
  public selects: Array<Select> = [];
  public expanded = false;
  public disabled = false;

  @ViewChild('roles') roles: MatAccordion;
  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    public dialogRef: MatDialogRef<RolesEditComponent>,
    public rolesService: RolesService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {}

  ngOnInit() {
    this.constants.title = this.appConfig.LANG.roles.title;

    this.constants.action = this.data.action;

    this.rolForm = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(200)
      ])
    });

    if (this.constants.action === this.appConfig.ACTIONS.add.id) {
      this.constants.title =
        this.appConfig.LANG.title.add + ' ' + this.constants.title;
      this.rol = {
        id: '',
        name: '',
        description: '',
        permitions: []
      };
    } else {
      this.rolForm.addControl('id', new FormControl('', [Validators.required]));

      this.rol = {
        id: this.data.item.id,
        name: this.data.item.attributes.name,
        description: this.data.item.attributes.description,
        permitions: this.generalsService.debugAccess(
          this.data.item.attributes.permissions
        )
      };

      this.rolForm.patchValue({
        id: this.rol.id,
        name: this.rol.name,
        description: this.rol.description
      });

      if (this.constants.action === this.appConfig.ACTIONS.detail.id) {
        this.constants.title =
          this.appConfig.LANG.title.detail + ' ' + this.constants.title;
        this.rolForm.controls.id.disable();
        this.rolForm.controls.name.disable();
        this.rolForm.controls.description.disable();
        this.disabled = true;
      } else {
        this.constants.title =
          this.appConfig.LANG.title.edit + ' ' + this.constants.title;
      }
    }

    const menu = new SetMenu();
    this.selectInit(this.generalsService.debugRoles(menu.menu));
  }

  onSubmit() {
    const form = this.rolForm;
    if (form.dirty && form.valid) {
      form.value.permitions = this.getPermitions(this.selects);
      this.generalsService.save(
        this.constants.action,
        form,
        this.dialogRef,
        this.rolesService
      );
    } else {
      this.close();
    }
  }

  close() {
    this.dialogRef.close(300);
  }

  getPermitions(actions: any): Array<Permition> {
    let permitions: Array<string> = [];
    const access: Array<Permition> = [
      { code: this.appConfig.SERVICES.permissions.findbyusername.context }
    ];
    actions.forEach((parent: any) => {
      let item: Array<string> = [];
      if (parent.type === this.appConfig.TYPE_SINGLE) {
        item = item.concat(this.getPermition(parent.action));
      } else if (parent.type === this.appConfig.TYPE_ACCORDION) {
        parent.child.forEach((child: any) => {
          item = item.concat(this.getPermition(child.action));
        });
        permitions = permitions.concat(item);
      }
    });

    permitions = Array.from(new Set(permitions));
    permitions.forEach(element => {
      access.push({ code: element });
    });
    return access;
  }

  getPermition(item: any) {
    let result: Array<string> = [];
    if (item.primary.select) {
      result = result.concat(item.primary.service);
    }

    item.second.forEach((element: any) => {
      if (element.select) {
        result = result.concat(element.service);
      }
    });
    return result;
  }

  setAction(element: any) {
    const actions: Access = {
      primary: {
        id: '',
        name: '',
        icon: '',
        select: false,
        service: []
      },
      second: []
    };

    let action: any = {};
    const primary = element.access.primary;
    const validatePrimary = this.validatePermition(primary);

    action = this.appConfig.ACTIONS[primary.type];
    actions.primary = {
      id: action.id,
      name: action.name,
      icon: action.icon,
      select: validatePrimary,
      service: primary.service
    };

    const second = element.access.second;
    if (second.length > 0) {
      second.forEach((elementSecond: any) => {
        action = this.appConfig.ACTIONS[elementSecond.type];
        actions.second.push({
          id: action.id,
          name: action.name,
          icon: action.icon,
          select: validatePrimary
            ? this.validatePermition(elementSecond)
            : false,
          service: elementSecond.service
        });
      });
    }
    return actions;
  }

  validatePermition(item: any) {
    const length = item.service.length;
    let count = 0;
    item.service.forEach((element: any) => {
      if (this.rol.permitions.indexOf(element) >= 0) {
        count++;
      }
    });
    return length === count;
  }

  returnValidate(count: number, length: number): ActionView {
    if (count === 0) {
      return { select: false, indeterminate: false };
    } else if (length === count) {
      return { select: true, indeterminate: false };
    } else {
      return { select: false, indeterminate: true };
    }
  }

  validateSingle(item: any): ActionView {
    if (item && item.primary) {
      const length = item.second.length + 1;
      let count = 0;
      if (item.primary.select) {
        count++;
      }
      item.second.forEach((element: any) => {
        if (element.select) {
          count++;
        }
      });

      return this.returnValidate(count, length);
    }
    return { select: false, indeterminate: false };
  }

  validateParent(childs: any, actions: any): ActionView {
    let select = 0,
      indeterminate = 0,
      length = 0;
    if (childs) {
      if (actions === undefined && childs.length > 0) {
        length = childs.length;
        childs.forEach((element: any) => {
          if (element.select) {
            select++;
          }
          if (element.indeterminate) {
            indeterminate++;
          }
        });
        if (select === 0 && indeterminate === 0) {
          return { select: false, indeterminate: false };
        } else if (length === select) {
          return { select: true, indeterminate: false };
        } else {
          return { select: false, indeterminate: true };
        }
      } else if (actions) {
        return this.validateSingle(actions);
      }
    }
    return { select: false, indeterminate: false };
  }

  selectInit(menu: Array<Menu>) {
    const roles: Array<Select> = [];
    menu.forEach((element: any) => {
      let actions: Access;
      const childs: Array<Child> = [];
      if (element.type === this.appConfig.TYPE_ACCORDION) {
        element.child.forEach((child: any) => {
          const action = this.setAction(child);
          const validateChild = this.validateSingle(action);
          childs.push({
            id: child.id,
            name: child.name,
            icon: child.icon,
            select: validateChild.select,
            indeterminate: validateChild.indeterminate,
            action: action
          });
        });
      } else if (element.type === this.appConfig.TYPE_SINGLE) {
        actions = this.setAction(element);
      }
      if (childs.length > 0 || actions) {
        const validateParent = this.validateParent(childs, actions);
        roles.push({
          id: element.id,
          name: element.name,
          type: element.type,
          icon: element.icon,
          select: validateParent.select,
          indeterminate: validateParent.indeterminate,
          action: actions,
          child: childs
        });
      }
    });
    this.selects = roles;
  }

  selectAction(
    type: string,
    idItem: any,
    idAction: any,
    event: any,
    idChild?: any
  ) {
    const role = this.selects[idItem],
      checked = event.checked;
    let actions = 0,
      actionLength = 0,
      newRole: any;

    let action: any;

    this.rolForm.markAsDirty();
    if (
      type === this.appConfig.TYPE_PRIMARY ||
      type === this.appConfig.TYPE_SECOND
    ) {
      if (role.type === this.appConfig.TYPE_ACCORDION) {
        const child = role.child[idChild];
        if (child) {
          newRole = child;
          if (type === this.appConfig.TYPE_PRIMARY) {
            action = child.action.primary;

            const second = child.action.second;
            if (!checked && second.length > 0) {
              for (let i = 0; i < second.length; i++) {
                second[i].select = checked;
              }
            }
          } else {
            action = child.action.second[idAction];

            const primary = child.action.primary;
            if (checked) {
              primary.select = checked;
            }
          }
        }
      } else {
        newRole = role;
        if (type === this.appConfig.TYPE_PRIMARY) {
          action = role.action[type];
          const second = role.action.second;
          if (!checked) {
            for (let i = 0; i < second.length; i++) {
              second[i].select = checked;
            }
          }
        } else {
          action = role.action[type][idAction];

          const primary = role.action.primary;
          if (checked) {
            primary.select = checked;
          }
        }
      }
      if (action) {
        action.select = checked;

        actionLength = newRole.action.second.length + 1;
        if (newRole.action.primary.select) {
          actions++;
        }
      }

      newRole.action.second.forEach((item: any) => {
        if (item.select) {
          actions++;
        }
      });

      newRole.select = false;
      newRole.indeterminate = false;
      if (actions < actionLength && actions > 0) {
        newRole.indeterminate = true;
      } else if (actions === actionLength) {
        newRole.select = true;
      }

      if (role.type === this.appConfig.TYPE_ACCORDION) {
        const roles = this.selects[idItem];
        let roleSelect = 0,
          roleIndeterminate = 0;
        roles.child.forEach((element: any) => {
          if (element.select) {
            roleSelect++;
          }

          if (element.indeterminate) {
            roleIndeterminate++;
          }
        });

        roles.select = false;
        roles.indeterminate = false;
        if (roles.child.length === roleSelect) {
          roles.select = true;
        } else if (
          role.child.length >= roleIndeterminate &&
          roleIndeterminate > 0
        ) {
          roles.indeterminate = true;
        } else if (role.child.length > roleSelect && roleSelect > 0) {
          roles.indeterminate = true;
        }
      }
    }
  }

  selectChild(idItem: any) {
    const role = this.selects[idItem],
      childLength = role.child.length;
    let childs = 0,
      indeterminate = 0;

    this.rolForm.markAsDirty();

    role.child.forEach(child => {
      if (child.select) {
        childs++;
      }
    });

    if (childs === 0) {
      role.child.forEach(child => {
        if (child.indeterminate) {
          indeterminate++;
        }
      });

      role.select = false;
      role.indeterminate = false;
      if (indeterminate <= childLength && indeterminate > 0) {
        role.indeterminate = true;
      }
    } else if (childs < childLength) {
      role.select = false;
      role.indeterminate = true;
    } else if (childs === childLength) {
      role.select = true;
      role.indeterminate = false;
    }
  }

  selectItemAllAction(idItem: any, event: any) {
    const role = this.selects[idItem],
      checked = event.checked;

    this.rolForm.markAsDirty();

    role.select = checked;
    role.indeterminate = false;
    if (role.type === this.appConfig.TYPE_ACCORDION) {
      for (let i = 0; i < role.child.length; i++) {
        role.child[i].indeterminate = false;
        role.child[i].select = checked;

        role.child[i].action.primary.select = checked;
        for (let j = 0; j < role.child[i].action.second.length; j++) {
          role.child[i].action.second[j].select = checked;
        }
      }
    } else if (role.type === this.appConfig.TYPE_SINGLE) {
      role.action.primary.select = checked;
      for (let i = 0; i < role.action.second.length; i++) {
        role.action.second[i].select = checked;
      }
    }
  }

  selectChildAllAction(idItem: any, idChild: any, event: any) {
    const child = this.selects[idItem].child[idChild];
    if (child) {
      const checked = event.checked;

      this.rolForm.markAsDirty();

      child.select = checked;
      child.indeterminate = false;
      child.action.primary.select = checked;
      for (let i = 0; i < child.action.second.length; i++) {
        child.action.second[i].select = checked;
      }

      this.selectChild(idItem);
    }
  }

  toggleAllPanels() {
    if (!this.expanded) {
      this.roles.openAll();
      this.expanded = true;
    } else {
      this.roles.closeAll();
      this.expanded = false;
    }
  }

  export() {
    this.generalsService.export('rol_' + this.rol.name, this.debugExport());
  }

  debugExport(): string {
    let result = 'MENU;PADRE;ACCION\n';
    this.selects.forEach(element => {
      if (element.type === this.appConfig.TYPE_ACCORDION) {
        element.child.forEach(child => {
          result = result + this.getActionItem(child, element.name);
        });
      } else if (element.type === this.appConfig.TYPE_SINGLE) {
        result = result + this.getActionItem(element);
      }
    });
    return result;
  }

  getActionItem(element: any, parent: string = ''): string {
    let result = '';
    if (element.action.primary.select) {
      result =
        result +
        element.name +
        ';' +
        parent +
        ';' +
        element.action.primary.name +
        '\n';
    }

    element.action.second.forEach((second: any) => {
      if (second.select) {
        result =
          result + element.name + ';' + parent + ';' + second.name + '\n';
      }
    });
    return result;
  }
}
